import { useEffect, useRef } from "react";
import { drawBlock } from "../game/engine/draw";
import { drawComet, DEFAULT_SKIN } from "../lib/comet";

// Miniature background "demo scene" for auth pages — reuses the same draw
// helpers the real GameCanvas uses so the aesthetic matches exactly.
// Purely decorative, pointer-events-none.
export default function AuthSceneBackground() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Scatter rotating blocks across the top/sides, avoiding the centered form.
    const makeBlocks = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      const positions = [
        { side: "left", x: W * 0.08, y: H * 0.18, tier: 1 },
        { side: "left", x: W * 0.14, y: H * 0.42, tier: 2 },
        { side: "left", x: W * 0.1, y: H * 0.7, tier: 1 },
        { side: "left", x: W * 0.22, y: H * 0.28, tier: 3 },
        { side: "left", x: W * 0.06, y: H * 0.58, tier: 2 },
        { side: "right", x: W * 0.92, y: H * 0.22, tier: 2 },
        { side: "right", x: W * 0.86, y: H * 0.48, tier: 1 },
        { side: "right", x: W * 0.78, y: H * 0.32, tier: 3 },
        { side: "right", x: W * 0.9, y: H * 0.74, tier: 1 },
        { side: "right", x: W * 0.82, y: H * 0.64, tier: 2 },
      ];
      return positions.map((p) => ({
        x: p.x - 30,
        y: p.y - 11,
        w: 58,
        h: 22,
        tier: p.tier,
        hp: p.tier,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (0.005 + Math.random() * 0.01) * (Math.random() < 0.5 ? -1 : 1),
        bobPhase: Math.random() * Math.PI * 2,
        bobAmp: 10 + Math.random() * 18,
      }));
    };
    let blocks = makeBlocks();
    const onResize = () => {
      resize();
      blocks = makeBlocks();
    };
    window.addEventListener("resize", onResize);

    // Two comets sweeping across on staggered sinusoidal paths.
    const comets = [
      { t: 0, speed: 0.0035, yAmp: 70, yBase: 0.25, trail: [] },
      { t: -0.35, speed: 0.0028, yAmp: 90, yBase: 0.78, trail: [] },
    ];

    const tick = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      ctx.clearRect(0, 0, W, H);

      // Soft vignette echoing the in-game background
      const vignette = ctx.createRadialGradient(W / 2, H * 0.75, 20, W / 2, H * 0.75, Math.max(W, H));
      vignette.addColorStop(0, "rgba(176,38,255,0.05)");
      vignette.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, W, H);

      // Blocks — rotate + vertical bob
      const now = performance.now();
      for (const b of blocks) {
        b.rot += b.rotSpeed;
        const bobbedY = b.y + Math.sin(now * 0.001 + b.bobPhase) * (b.bobAmp / 3);
        drawBlock(ctx, { ...b, y: bobbedY });
      }

      // Comets — sinusoidal sweeps with trails
      for (const c of comets) {
        c.t += c.speed;
        const cx = ((c.t * (W + 200)) % (W + 260)) - 130;
        const cy = H * c.yBase + Math.sin(c.t * 2.2) * c.yAmp;
        c.trail.push({ x: cx, y: cy });
        if (c.trail.length > 22) c.trail.shift();
        const vx = (W + 200) * c.speed;
        const vy = Math.cos(c.t * 2.2) * c.yAmp * 2.2 * c.speed;
        drawComet(ctx, DEFAULT_SKIN, cx, cy, 12, {
          angle: Math.atan2(vy, vx),
          trail: c.trail,
          glowBoost: 1.1,
        });
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 w-screen h-screen pointer-events-none"
      style={{ opacity: 0.8 }}
      data-testid="auth-scene-bg"
    />
  );
}
